import React from 'react';

import DialogActions from '@mui/material/DialogActions';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Avatar from 'src/components/ui/Avatar';
import Button from 'src/components/ui/Button';
import Typography from 'src/components/ui/Typography';

import useAlerts from 'src/hooks/useAlerts';
import { useCreateRecommendationMutation } from 'src/hooks/__generated__/queries';
import type { Medium, UserProfileQuery, CompanyProfileQuery } from 'src/types/__generated__/graphql';
import type { RecommendationModalOpenedType } from 'src/components/profile/recommendations/RecommendationsModal';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

interface Props {
    user?: NonNullable<UserProfileQuery['userProfile']>;
    company?: NonNullable<CompanyProfileQuery['companyProfile']>;
    handleBack: () => void;
    handleNext: () => void;
    modalOpenedType: RecommendationModalOpenedType;
}

const RecommendationConfirmation: React.FC<Props> = ({ user, company, handleBack, handleNext, modalOpenedType }) => {
    const { t } = useTranslation(['profile', 'common']);
    const { addAlert } = useAlerts();
    const { logEventDynamic } = useDynamicLogEvents();

    const recommendeeDisplayName = user?.fullName ?? company?.name;

    const [createRecommendation] = useCreateRecommendationMutation();

    const handleRecommend = async () => {
        try {
            const refetchQueries = ['RecommendingUsers'];
            if (user) {
                refetchQueries.push('UserProfile');
            } else if (company) {
                refetchQueries.push('CompanyProfile');
            }
            await createRecommendation({
                variables: {
                    userId: user?.id,
                    companyId: company?.id,
                },
                refetchQueries,
                awaitRefetchQueries: true,
            });
            logEventDynamic(AnalyticsEvent.RecommendConfirmation);
            addAlert({
                type: 'success',
                message: t('profile:recommendSuccess'),
            });
            handleNext();
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String((error as Error).message),
            });
        }
    };
    return (
        <>
            <Box display="flex" flexDirection="column" alignItems="center" p={3}>
                <Avatar
                    image={(user?.image ?? company?.image) as Medium}
                    alt={recommendeeDisplayName}
                    fallbackText={user?.initials ?? company?.initials}
                    isCompany={Boolean(company)}
                    size={64}
                    border
                />
                <Box mt={1}>
                    <Typography variant="h4" align="center">
                        {recommendeeDisplayName}
                    </Typography>
                </Box>
            </Box>
            <Box mb={4} maxWidth="20rem" mx="auto">
                <Typography align="center">
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t(
                                `profile:${
                                    modalOpenedType === 'fromRatings' ? 'recommendContent' : 'recommendConfirmation'
                                }`,
                                { fullName: recommendeeDisplayName },
                            ),
                        }}
                    />
                </Typography>
            </Box>
            <DialogActions>
                <Button onClick={handleBack} variant="outlined">
                    {t('common:cancel')}
                </Button>
                <Button onClick={handleRecommend}>{t('profile:recommend')}</Button>
            </DialogActions>
        </>
    );
};

export default RecommendationConfirmation;
